import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
    HomeIcon, VideoCameraIcon, AcademicCapIcon, 
    TicketIcon, UsersIcon, ChatBubbleLeftRightIcon,
    ArrowLeftOnRectangleIcon, CodeBracketIcon, Bars3BottomRightIcon, XMarkIcon,
    RectangleStackIcon, ShieldCheckIcon, UserCircleIcon, LockClosedIcon, CalendarIcon, DocumentCheckIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { signOut, onAuthStateChanged } from 'firebase/auth'; 
import { auth, db } from '../firebase'; 
import { 
    doc, collection, getDocs, serverTimestamp, runTransaction, onSnapshot 
} from 'firebase/firestore';
const Sidebar = ({ userRole }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [user, setUser] = useState(null);
    const [isActivating, setIsActivating] = useState(false);
    const [activationCode, setActivationCode] = useState("");
    const [alertConfig, setAlertConfig] = useState({ show: false, msg: '', type: 'success' });

    // [UNIFIED_VERSION] - توحيد الإصدار مع الداشبورد والمنصة لعام 2026
    const appVersion = "MK@AlBARON DEVELOPMENT 2026 | VERSION 2.1.0"; 

    // ⬇️ [EFFECT: USER_SYNC] - (مزامنة بيانات المستخدم والاشتراك)
    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
            if (currentUser) {
                const unsubDoc = onSnapshot(doc(db, "users", currentUser.uid), (docSnap) => {
                    if (docSnap.exists()) {
                        setUser({ uid: currentUser.uid, ...docSnap.data() });
                    }
                });
                return () => unsubDoc();
            }
        });
        return () => unsubscribeAuth();
    }, []);

    // ⬇️ [FUNCTION: HANDLE_ACTIVATE] - (دالة التفعيل الملكية بنطق البارون)
    const handleActivate = async () => {
        const cleanInput = activationCode.replace(/[-\s]/g, '').trim().toUpperCase();
        if (!cleanInput) {
            setAlertConfig({ show: true, msg: "يرجى إدخل الكود أولاً ⚠️", type: "error" });
            return;
        }
        setIsActivating(true);
        try {
            const tokensRef = collection(db, "tokens");
            const querySnapshot = await getDocs(tokensRef);
            const tokenDoc = querySnapshot.docs.find(doc => 
                doc.data().code.replace(/[-\s]/g, '').toUpperCase() === cleanInput
            );
            if (!tokenDoc) {
                setAlertConfig({ show: true, msg: "الكود غير موجود، تأكد من الأحرف ❌", type: "error" });
                setIsActivating(false); return;
            }
            const tokenData = tokenDoc.data();
            if (tokenData.status !== "available") {
                setAlertConfig({ show: true, msg: "هذا الكود مستخدم مسبقاً ❌", type: "error" });
                setIsActivating(false); return;
            }
            await runTransaction(db, async (transaction) => {
                const userRef = doc(db, "users", auth.currentUser.uid);
                const tokenRef = doc(db, "tokens", tokenDoc.id);
                const durationType = tokenData.duration;
                let userUpdates = {
                    isActivated: true, isSubscribed: true, active: true,
                    activatedAt: serverTimestamp(),
                    subscriptionType: durationType, activeCourse: durationType,
                    usedToken: tokenData.code 
                };
                if (durationType.includes("أول")) userUpdates.isCourse1Active = true;
                else if (durationType.includes("ثاني")) userUpdates.isCourse2Active = true;
                else if (durationType.includes("كورسين") || durationType.includes("كامل")) {
                    userUpdates.isCourse1Active = true; userUpdates.isCourse2Active = true;
                }
                transaction.update(userRef, userUpdates);
                transaction.update(tokenRef, { status: "used", usedBy: auth.currentUser.uid, usedAt: serverTimestamp() });
            });
            setAlertConfig({ show: true, msg: `تم تفعيل ${tokenData.duration} بنجاح! 🚀`, type: "success" });
            setActivationCode("");
        } catch (error) { setAlertConfig({ show: true, msg: "حدث خطأ في الاتصال 🌐", type: "error" });
        } finally { setIsActivating(false); }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            localStorage.clear();
            sessionStorage.clear();
            navigate('/login', { replace: true });
        } catch (error) { console.error("Logout Error:", error); }
    };

    const menuNavigate = (path) => {
        setIsOpen(false); 
        navigate(path);
    };

    // [STYLING_CONFIG] - (إعدادات الستايل والتباين الملكي للأزرار)
    const itemClass = "flex items-center gap-3 p-4 mb-1 cursor-pointer transition-all duration-300 rounded-[22px] font-black text-gray-800 dark:text-gray-100 hover:bg-white/20 dark:hover:bg-white/5 hover:text-[#007AFF] group text-right justify-start font-cairo";
    const activeClass = "bg-[#007AFF]/25 !text-[#007AFF] border border-[#007AFF]/30 shadow-lg backdrop-blur-md";
    const isChatActive = location.pathname === '/chat';
    const chatBtnClass = `${itemClass} mt-4 shadow-sm ${isChatActive ? 'bg-pink-500/25 !text-pink-500 border border-pink-500/30' : 'text-pink-500 bg-pink-500/5 hover:bg-pink-500/10'}`;

    return (
        <>
            {/* 📱 [MOBILE_FLOATING_BUTTON] - (الزر العائم الملكي للموبايل) */}
            <div className="lg:hidden fixed bottom-8 left-7 z-[1200]" dir="rtl">
                <motion.button 
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setIsOpen(!isOpen)} 
                    className="flex items-center gap-3 py-3.5 px-6 rounded-[28px] shadow-[0_10px_30px_rgba(58,153,255,0.4)] transition-all border border-white/30 relative overflow-hidden group font-cairo"
                    style={{ background: 'linear-gradient(135deg, #3A99FF 0%, #007AFF 100%)', backdropFilter: 'blur(20px)' }}
                >
                    <span className="font-black text-[14px] text-white relative z-10 font-cairo">القائمة</span>
                    <div className="w-[1.5px] h-4 bg-white/40 ml-1 relative z-10"></div>
                    <div className="relative z-10 text-white">
                        {isOpen ? <XMarkIcon className="w-6 h-6 stroke-[3]" /> : <Bars3BottomRightIcon className="w-6 h-6 stroke-[3]" />}
                    </div>
                </motion.button>
            </div>

            {/* 🏰 [SIDEBAR_CONTAINER] - (السايدبار الزجاجي الرئيسي) */}
            <aside 
                className={`fixed inset-y-0 right-0 z-[1100] w-72 flex flex-col transition-transform duration-500 ease-in-out ${isOpen ? 'translate-x-0 shadow-2xl' : 'translate-x-full'} lg:relative lg:translate-x-0 lg:flex lg:m-4 lg:rounded-[30px] lg:h-[90vh] lg:my-[5vh] border-none font-cairo overflow-hidden`} 
                dir="rtl"
            >
                <div className="absolute inset-0 -z-10 backdrop-blur-[40px] bg-white/40 dark:bg-black/40 border-l lg:border border-white/30 dark:border-white/10 lg:rounded-[30px]" />

                {/* 1️⃣ [HEADER_SECTION] - (الهيدر والشعار) */}
                <div className="p-8 flex flex-col items-center justify-center border-b border-white/10 shrink-0 bg-transparent">
                    <img src="/Logo.png" alt="Logo" className="w-[65px] h-[65px] object-contain drop-shadow-xl" />
                    <div className="mt-3 px-4 py-1 bg-[#007AFF]/15 rounded-full font-cairo border border-[#007AFF]/30 backdrop-blur-md">
                        <span className="text-[10px] font-black text-[#007AFF] uppercase tracking-wider font-cairo">
                            {userRole === 'teacher' ? 'Teacher Panel' : 'Student Portal'}
                        </span>
                    </div>
                </div>

                {/* 2️⃣ [NAVIGATION_LINKS] - (الروابط والملاحة الذكية) */}
                <div className="flex-1 overflow-y-auto custom-scroll p-4 text-right font-cairo bg-transparent">
                    <nav className="space-y-1 font-cairo text-right">
                        {/* 🛠️ [SECTION: TEACHER_ROUTES] - مسارات لوحة تحكم الأستاذ المحدثة 2026 */}
                        {userRole === 'teacher' ? (
                            <>
                                <div onClick={() => menuNavigate('/admin')} className={`${itemClass} ${location.pathname === '/admin' ? activeClass : ''}`}>
                                    <HomeIcon className="w-6 h-6 shrink-0 stroke-[2.5]" /> <span>لوحة الإحصائيات | Stats</span>
                                </div>
                                <div onClick={() => menuNavigate('/admin/tokens')} className={`${itemClass} ${location.pathname === '/admin/tokens' ? activeClass : ''}`}>
                                    <TicketIcon className="w-6 h-6 shrink-0 stroke-[2.5]" /> <span>توليد الأكواد | Tokens</span>
                                </div>
                                <div onClick={() => menuNavigate('/admin/lessons')} className={`${itemClass} ${location.pathname === '/admin/lessons' ? activeClass : ''}`}>
                                    <VideoCameraIcon className="w-6 h-6 shrink-0 stroke-[2.5]" /> <span>رفع المحاضرات | Lessons</span>
                                </div>
                                <div onClick={() => menuNavigate('/admin/quizzes')} className={`${itemClass} ${location.pathname === '/admin/quizzes' ? activeClass : ''}`}>
                                    <AcademicCapIcon className="w-6 h-6 shrink-0 stroke-[2.5]" /> <span>إدارة الاختبارات | Quizzes</span>
                                </div>
                                <div onClick={() => menuNavigate('/admin/students')} className={`${itemClass} ${location.pathname === '/admin/students' ? activeClass : ''}`}>
                                    <UsersIcon className="w-6 h-6 shrink-0 stroke-[2.5]" /> <span>إدارة الطلاب | Students</span>
                                </div>
                                
                                {/* ✨ [ADD_CHAT_FOR_TEACHER] - إضافة قسم المحادثة للأستاذ للوصول السريع للطلاب */}
                                <div onClick={() => menuNavigate('/chat')} className={`${itemClass} ${location.pathname === '/chat' ? 'bg-pink-500/25 !text-pink-500 border border-pink-500/30' : 'text-pink-500 bg-pink-500/5 hover:bg-pink-500/10'}`}>
                                    <ChatBubbleLeftRightIcon className="w-6 h-6 shrink-0 stroke-[2.5]" /> <span>دردشة الطلاب | Chat</span>
                                </div>
                                
                                                            </>
          
                        ) : (
                            <>
                                {/* 🎓 [SECTION: STUDENT_FEATURES] - (روابط خدمات الطالب المفعّلة) */}
                                {(user?.isSubscribed || user?.activeCourse) && (
                                    <>
                                        <div onClick={() => menuNavigate('/student')} className={`${itemClass} ${location.pathname === '/student' ? activeClass : ''}`}>
                                            <HomeIcon className="w-6 h-6 shrink-0 stroke-[2.5]" /> <span>الرئيسية | Home</span>
                                        </div>
                                        <div onClick={() => menuNavigate('/student/courses')} className={`${itemClass} ${location.pathname === '/student/courses' ? activeClass : ''}`}>
                                            <AcademicCapIcon className="w-6 h-6 shrink-0 stroke-[2.5]" /> <span>كورساتي المفعّلة</span>
                                        </div>
                                        <div onClick={() => menuNavigate('/student/schedule')} className={`${itemClass} ${location.pathname === '/student/schedule' ? activeClass : ''}`}>
                                            <CalendarIcon className="w-6 h-6 shrink-0 stroke-[2.5]" /> <span>جدول المحاضرات</span>
                                        </div>
                                        <div onClick={() => menuNavigate('/student/quizzes')} className={`${itemClass} ${location.pathname === '/student/quizzes' ? activeClass : ''}`}>
                                            <DocumentCheckIcon className="w-6 h-6 shrink-0 stroke-[2.5]" /> <span>الامتحانات</span>
                                        </div>
                                        <div onClick={() => menuNavigate('/chat')} className={chatBtnClass}>
                                            <ChatBubbleLeftRightIcon className="w-6 h-6 shrink-0 stroke-[2.5]" /> <span>المحادثة المباشرة</span>
                                        </div>
                                    </>
                                )}

                                {/* 🍏 [SECTION: ACTIVATION_STATUS_LOGIC] - (منطق بطاقة التفعيل الذكية المحدثة 2026) */}
                                {(user?.isCourse1Active && user?.isCourse2Active) || user?.subscriptionType?.includes("كامل") ? (
                                    // ✨ [ROYAL_SUCCESS_CARD]: تظهر عند اكتمال تفعيل الكورسين وتخفي البطاقة القديمة
                                    <motion.div 
                                        initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                                        className="mt-6 p-5 rounded-[28px] bg-[#007AFF]/10 border border-[#007AFF]/30 backdrop-blur-md flex flex-col items-center text-center space-y-2"
                                    >
                                        <div className="w-10 h-10 bg-[#007AFF] rounded-full flex items-center justify-center shadow-lg shadow-[#007AFF]/30">
                                            <ShieldCheckIcon className="w-6 h-6 text-white" />
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-[13px] font-black text-[#007AFF] font-cairo">تم تفعيل الكورسين بنجاح ✅</p>
                                            <p className="text-[10px] text-gray-500 dark:text-gray-400 font-bold font-cairo uppercase tracking-tighter">Full Access Activated 2026</p>
                                        </div>
                                    </motion.div>
                                ) : (
                                    // 🎫 [ACTIVATION_CARD]: تظهر إذا كان الطالب غير مفعل أو لديه كورس واحد فقط متبقي
                                    <div className="mt-6 p-5 rounded-[28px] bg-gradient-to-br from-[#FF9500]/10 to-[#FF2D55]/10 border border-[#FF9500]/20 backdrop-blur-md">
                                        <div className="flex items-center gap-3 mb-3 text-right">
                                            <TicketIcon className="w-6 h-6 text-[#FF9500]" />
                                            <span className="font-black text-[13px] text-[#FF9500] font-cairo">
                                                {(!user?.isCourse1Active && !user?.isCourse2Active) 
                                                    ? "تفعيل الاشتراك | Activation" 
                                                    : "تفعيل الكود المتبقي"}
                                            </span>
                                        </div>
                                        
                                        <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 mb-4 px-1 leading-relaxed font-cairo text-right">
                                            {(!user?.isCourse1Active && !user?.isCourse2Active)
                                                ? "يرجى إدخال كود الاشتراك المكون من 8 أرقام لبدء رحلتك الدراسية."
                                                : user?.isCourse1Active 
                                                    ? "أدخل كود الكورس الثاني لفتح الوصول الكامل للأكاديمية."
                                                    : "أدخل كود الكورس الأول لفتح الوصول الكامل للأكاديمية."
                                            }
                                        </p>
                                        
                                        <div onClick={() => menuNavigate('/student/activate')} 
                                             className="w-full py-3 bg-[#FF9500] text-white rounded-xl font-black text-xs text-center shadow-lg shadow-[#FF9500]/20 active:scale-95 transition-all cursor-pointer font-cairo">
                                            اذهب للتفعيل الآن | Activate 🚀
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </nav>
                </div>

                {/* 3️⃣ [ROYAL_FOOTER] - (الفوتر الملكي المحدث لعام 2026) */}
                <div className="p-4 pb-8 border-t border-white/10 shrink-0 text-center font-cairo bg-transparent">
                    <div onClick={handleLogout} className="flex items-center gap-3 p-3.5 mb-6 cursor-pointer rounded-[20px] font-black text-red-600 hover:bg-red-500/10 justify-center border border-red-500/20 transition-all active:scale-95 font-cairo backdrop-blur-sm">
                        <ArrowLeftOnRectangleIcon className="w-5 h-5 shrink-0 stroke-[3]" /> 
                        <span>تسجيل الخروج</span>
                    </div>

                    <div className="flex flex-col items-center gap-4 bg-transparent">
                        <motion.a 
                            whileHover={{ scale: 1.1, rotate: 5 }} 
                            whileTap={{ scale: 0.9 }}
                            href="https://wa.me/9647739661174" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-10 h-10 rounded-xl bg-[#25D366]/15 text-[#25D366] flex items-center justify-center border border-[#25D366]/20 shadow-sm shadow-[#25D366]/10"
                        >
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.246 2.248 3.484 5.232 3.484 8.412-.003 6.557-5.338 11.892-11.893 11.892-1.997-.001-3.951-.5-5.688-1.448l-6.309 1.656zm6.29-4.143c1.589.944 3.119 1.462 4.827 1.463 5.483 0 9.943-4.461 9.946-9.943.001-2.654-1.034-5.147-2.915-7.028-1.88-1.881-4.37-2.918-7.027-2.919-5.482 0-9.944 4.463-9.947 9.945 0 2.028.613 3.738 1.769 5.339l-1.017 3.714 3.811-1.001zM16.4 12.87c-.282-.141-1.67-.824-1.928-.918-.258-.094-.446-.141-.634.141-.188.282-.728.918-.893 1.105-.165.188-.329.212-.611.071-.282-.141-1.194-.44-2.274-1.402-.841-.75-1.408-1.676-1.573-1.958-.165-.282-.018-.434.123-.574.127-.127.282-.329.424-.494.141-.165.188-.282.282-.471.094-.188.047-.353-.024-.494-.071-.141-.634-1.529-.869-2.094-.23-.553-.465-.477-.634-.486-.163-.009-.353-.01-.541-.01s-.494.071-.753.353c-.259.282-.988.965-.988 2.353 0 1.388 1.012 2.729 1.153 2.918.141.188 1.991 3.041 4.824 4.261.674.29 1.2.463 1.611.592.678.215 1.295.185 1.782.112.543-.081 1.67-.682 1.905-1.341.235-.659.235-1.224.165-1.341-.071-.117-.259-.188-.541-.329z" />
                            </svg>
                        </motion.a>

                        <div className="flex flex-col items-center gap-1 opacity-80 font-cairo bg-transparent">
                            <CodeBracketIcon className="w-4 h-4 text-[#007AFF] mb-1 font-cairo shadow-blue-500/20" />
                            <span className="text-[9px] font-black tracking-widest text-gray-900 dark:text-white uppercase font-cairo">
                                {appVersion}
                            </span>
                        </div>
                    </div>
                </div>
            </aside>

            {/* [OVERLAY_MOBILE] - (خلفية التعتيم للموبايل) */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsOpen(false)} className="fixed inset-0 bg-black/70 backdrop-blur-md z-[1050] lg:hidden" />
                )}
            </AnimatePresence>
        </>
    );
};

export default Sidebar;